import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { FieldPath } from 'react-hook-form';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { CompanyFundMetricsDataModel } from '../../../schemas/CompanyFundMetrics.schema';
import { useAllCompanyDetailsFields } from '../../CompanyProfiles2/Summary/useCompanyDetailsFields';
import { useAllInvestmentMetricsFields } from '../../CompanyProfiles2/Summary/useInvestmentMetricsFields';
import { ICompanyViewModel } from '../../../view-models/company.view-model';
import { getCompanyListsFieldsFromSchema } from '../../CompanyProfiles2/Summary/useCompanyListsFields';
import {
  selectedDetailsFieldsOrgState,
  selectedListOverviewFieldsOrgState,
  selectedMetricsFieldsOrgState,
} from './DataDisplayState';

export function useLoadSelectedDetailsFields() {
  const [selectedFields, setSelectedFields] = useAtom(selectedDetailsFieldsOrgState);
  const allFields = useAllCompanyDetailsFields();

  const [loaded, setLoaded] = useState(Boolean(selectedFields?.length));

  useEffect(() => {
    if (!selectedFields?.length) {
      setSelectedFields(allFields.map((field) => field.key as FieldPath<ICompanyDataModel>));
      setLoaded(true);
    }
  }, [allFields, selectedFields?.length, setSelectedFields]);

  return { loaded, selectedFields };
}

export function useLoadSelectedMetricsFields() {
  const [selectedFields, setSelectedFields] = useAtom(selectedMetricsFieldsOrgState);
  const allFields = useAllInvestmentMetricsFields();

  const [loaded, setLoaded] = useState(Boolean(selectedFields?.length));

  useEffect(() => {
    if (!selectedFields?.length) {
      setSelectedFields(allFields.map((field) => field.key as FieldPath<CompanyFundMetricsDataModel>));
      setLoaded(true);
    }
  }, [allFields, selectedFields?.length, setSelectedFields]);

  return { loaded, selectedFields };
}

export function getPageWithChange(staleOrder: string[], newOrder: string[], itemsPerPage: number) {
  for (let i = 0; i < staleOrder.length; i++) {
    if (staleOrder[i] !== newOrder[i]) {
      return getPageForIndex(i, itemsPerPage);
    }
  }
  return 1;
}

export function useLoadSelectedListOverviewFieldsFields() {
  const [selectedFields, setSelectedFields] = useAtom(selectedListOverviewFieldsOrgState);
  const allFields = getCompanyListsFieldsFromSchema();

  const [loaded, setLoaded] = useState(Boolean(selectedFields?.length));

  useEffect(() => {
    if (!selectedFields?.length) {
      setSelectedFields(allFields.map((field) => field.key as FieldPath<ICompanyViewModel>));
      setLoaded(true);
    }
  }, [allFields, selectedFields?.length, setSelectedFields]);

  return { loaded, selectedFields };
}

export function getPageForIndex(index: number, itemsPerPage: number) {
  return Math.floor(index / itemsPerPage) + 1;
}

/**
 * The selected keys that still resolve to a field. A key whose field is gone — a deleted KPI or
 * custom field — is ignored, so the next save writes it out of the preference.
 *
 * An empty `allFields` resolves nothing, so the keys are kept: it means either a section with no
 * field list to check against, or one whose fields have not loaded yet.
 */
export function resolvableSelectedFields(selectedFields: string[], allFields: { key: string }[]) {
  if (allFields.length === 0) {
    return selectedFields;
  }
  const fieldKeys = new Set(allFields.map((field) => field.key));

  return selectedFields.filter((key) => fieldKeys.has(key));
}
